import { AlertCircleIcon } from "lucide-react-native";
import { memo } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
} from "react-hook-form";

import Screen from "@/features/auth/components/organisms/Screen";

import CustomSelect from "@/components/molecules/CustomSelect";
// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound-button waiver: Button/ButtonText with ButtonText children + className — not expressible via the closed Button atom (no…
import { Button, ButtonText } from "@/components/ui/button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "@/components/atoms/FormControl";
import { Input } from "@/components/atoms/Input";
import { Stack } from "@/components/atoms/Stack";
import { USER_TITLE_OPTIONS } from "@/config/userTitles";
import { ifWeb } from "@/utils/platform";

/**
 *
 */
export interface IForm {
  firstName: string;
  lastName: string;
  title: string;
}

/**
 *
 */
interface Props {
  control: Control<IForm>;
  valid: boolean;
  widthContent: number;
  onSubmit: () => void;
  loading: boolean;
  isDark: boolean;
  /** The memoized first-name field renderer, built by the Container. */
  renderFirstName: ReturnType<typeof makeFirstNameField>;
  /** The memoized last-name field renderer, built by the Container. */
  renderLastName: ReturnType<typeof makeLastNameField>;
  /** The memoized title field renderer, built by the Container. */
  renderTitle: ReturnType<typeof makeTitleField>;
}

/** What each of the registration fields needs from the screen. */
export interface RegisterFieldOptions {
  /** Form validation errors. */
  readonly errors: FieldErrors<IForm>;
  /** Whether the dark theme is active. */
  readonly isDark: boolean;
  /** Whether the registration request is in progress. */
  readonly loading: boolean;
}

/**
 * Builds the `Controller` render prop for the firstName field.
 *
 * A factory so the Container can memoize it: `Controller` re-renders the field
 * whenever `render` changes identity, which is why this was a `useCallback` —
 * one that sat in JSX ATTRIBUTE position inside the View, and so was a hook in
 * a View, and a fragile one: wrap the field in a conditional and the hook order
 * breaks silently (SE-8019).
 * @param options - What the field needs from the screen.
 * @param options.errors - Form validation errors.
 * @param options.isDark - Whether the dark theme is active.
 * @param options.loading - Whether the request is in progress.
 * @returns The render prop.
 */
export const makeFirstNameField =
  ({ errors, isDark, loading }: RegisterFieldOptions) =>
  ({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "firstName">;
  }) => (
    <FormControl
      size="lg"
      isDisabled={false}
      isInvalid={!loading && !!errors.firstName}
      isReadOnly={false}
      isRequired={false}
    >
      <Input
        variant="outline"
        className={`border-0 border-none ${isDark ? "bg-surface-raised" : "bg-surface-subtle"}`}
        size="xl"
        field={{
          placeholder: "First Name",
          type: "text",
          value: value ?? "",
          keyboardType: "default",
          inputMode: "text",
          autoCorrect: true,
          autoComplete: "given-name",
          textContentType: "givenName",
          autoCapitalize: "words",
          returnKeyLabel: "Next",
          enterKeyHint: "next",
          onChangeText: onChange,
          onBlur,
          className: ifWeb("font-body text-title", "font-body"),
        }}
      />

      <FormControlError>
        <FormControlErrorIcon as={AlertCircleIcon} />
        <FormControlErrorText>{errors.firstName?.message}</FormControlErrorText>
      </FormControlError>
    </FormControl>
  );

/**
 * Builds the `Controller` render prop for the lastName field.
 *
 * A factory so the Container can memoize it: `Controller` re-renders the field
 * whenever `render` changes identity, which is why this was a `useCallback` —
 * one that sat in JSX ATTRIBUTE position inside the View, and so was a hook in
 * a View, and a fragile one: wrap the field in a conditional and the hook order
 * breaks silently (SE-8019).
 * @param options - What the field needs from the screen.
 * @param options.errors - Form validation errors.
 * @param options.isDark - Whether the dark theme is active.
 * @param options.loading - Whether the request is in progress.
 * @returns The render prop.
 */
export const makeLastNameField =
  ({ errors, isDark, loading }: RegisterFieldOptions) =>
  ({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "lastName">;
  }) => (
    <FormControl
      size="lg"
      isDisabled={false}
      isInvalid={!loading && !!errors.lastName}
      isReadOnly={false}
      isRequired={false}
    >
      <Input
        variant="outline"
        className={`border-0 border-none ${isDark ? "bg-surface-raised" : "bg-surface-subtle"}`}
        size="xl"
        field={{
          placeholder: "Last Name",
          type: "text",
          value: value ?? "",
          keyboardType: "default",
          inputMode: "text",
          autoCorrect: true,
          autoComplete: "family-name",
          textContentType: "familyName",
          autoCapitalize: "words",
          returnKeyLabel: "Next",
          enterKeyHint: "next",
          onChangeText: onChange,
          onBlur,
          className: ifWeb("font-body text-title", "font-body"),
        }}
      />

      <FormControlError>
        <FormControlErrorIcon as={AlertCircleIcon} />
        <FormControlErrorText>{errors.lastName?.message}</FormControlErrorText>
      </FormControlError>
    </FormControl>
  );

/**
 * Builds the `Controller` render prop for the title field.
 *
 * A factory so the Container can memoize it: `Controller` re-renders the field
 * whenever `render` changes identity, which is why this was a `useCallback` —
 * one that sat in JSX ATTRIBUTE position inside the View, and so was a hook in
 * a View, and a fragile one: wrap the field in a conditional and the hook order
 * breaks silently (SE-8019).
 * @param options - What the field needs from the screen.
 * @param options.errors - Form validation errors.
 * @param options.loading - Whether the request is in progress.
 * @returns The render prop.
 */
export const makeTitleField =
  ({ errors, loading }: RegisterFieldOptions) =>
  ({
    field: { onChange },
  }: {
    field: ControllerRenderProps<IForm, "title">;
  }) => (
    <FormControl
      size="lg"
      isDisabled={false}
      isInvalid={!loading && !!errors.title}
      isReadOnly={false}
      isRequired={false}
    >
      <CustomSelect
        placeholder="Function"
        options={USER_TITLE_OPTIONS}
        onChangeValue={onChange}
      />
      <FormControlError>
        <FormControlErrorIcon as={AlertCircleIcon} />
        <FormControlErrorText>{errors.title?.message}</FormControlErrorText>
      </FormControlError>
    </FormControl>
  );

/**
 * View component for user registration screen with first name, last name, and title inputs
 * @param root0 - Component props
 * @param root0.control - React Hook Form control object for managing form state
 * @param root0.valid - Boolean indicating whether the registration form is valid
 * @param root0.onSubmit - Callback fired when the complete button is pressed to submit registration
 * @param root0.loading - Boolean indicating registration is in progress
 * @param root0.widthContent - Width value for the content container in pixels
 * @param root0.isDark - Boolean indicating dark theme is active for conditional styling
 * @param root0.renderFirstName - The memoized first-name field renderer
 * @param root0.renderLastName - The memoized last-name field renderer
 * @param root0.renderTitle - The memoized title field renderer
 */
const RegisterView = ({
  control,
  valid,
  onSubmit,
  loading,
  widthContent,
  isDark,
  renderFirstName,
  renderLastName,
  renderTitle,
}: Props) => (
  <Screen
    title="Register"
    widthContent={widthContent}
    subTitle="Tell us a bit about yourself."
    footer={
      <Button
        isDisabled={loading || !valid}
        onPress={onSubmit}
        className={isDark ? "bg-surface-subtle" : "bg-surface-muted"}
        size="xl"
      >
        <ButtonText
          className={`font-body ${ifWeb("text-title", "")} ${!loading && valid ? "text-status-info" : "text-content-muted"}`}
        >
          {!loading ? "Complete" : "Please Wait..."}
        </ButtonText>
      </Button>
    }
  >
    <Stack className="gap-6">
      <Controller name="firstName" control={control} render={renderFirstName} />
      <Controller name="lastName" control={control} render={renderLastName} />
      <Controller name="title" control={control} render={renderTitle} />
    </Stack>
  </Screen>
);

RegisterView.displayName = "RegisterView";

export default memo(RegisterView);
